import { Mutation, Query } from "@apollo/client/react/components";
import { Loader } from "@govlaunch/core";
import { FORM_ERROR } from "final-form";
import { useRouter } from "next/router";
import React from "react";
import { useIntl } from "react-intl";
import EditGroup from "~/lib/mutations/EditGroup";
import { IEditGroupMutation, IEditGroupMutationVariables } from "~/lib/mutations/__generated__/EditGroup.generated";
import GroupQuery from "~/lib/queries/GroupQuery";
import { IGroupQuery, IGroupQueryVariables } from "~/lib/queries/__generated__/GroupQuery.generated";
import { IVendorQuery } from "~/lib/queries/__generated__/VendorQuery.generated";
import GroupForm from "~/components/group/GroupForm";
import { convertGroupToInitialValues, convertValuesToVariables } from "~/components/vendor/profile/utils";

interface IEditVendorGroupProps {
  groupSlug: string;
  vendor: NonNullable<IVendorQuery["company"]>;
}
export default function EditVendorGroup({ groupSlug, vendor }: IEditVendorGroupProps) {
  const intl = useIntl();
  const router = useRouter();

  const maxGroups = intl.formatMessage({
    defaultMessage: `You've reached the limit of groups for your plan`,
    id: "knb2lM",
  });
  const couldntSave = intl.formatMessage({
    defaultMessage: `Couldn't save. Please try again later!`,
    id: "R5RM9+",
  });

  return (
    <Mutation<IEditGroupMutation, IEditGroupMutationVariables> mutation={EditGroup}>
      {(editGroup) => (
        <Query<IGroupQuery, IGroupQueryVariables>
          query={GroupQuery}
          variables={{
            slug: groupSlug,
          }}
        >
          {({ data: group, loading }) => {
            if (loading) {
              return <Loader centered={true} />;
            }

            if (!group || !group.groupBySlug) {
              return null;
            }

            return (
              <div>
                <GroupForm
                  initialValues={convertGroupToInitialValues(group.groupBySlug)}
                  onSubmit={async (values) => {
                    try {
                      await editGroup({
                        variables: {
                          id: group.groupBySlug!._id,
                          group: {
                            ...convertValuesToVariables(values),
                            vendor: vendor._id,
                          },
                        },
                      });

                      router.push("/vendors/[vendorSlug]/[[...content]]", `/vendors/${vendor.slug}/groups`);

                      return null;
                    } catch (e: any) {
                      if (/Vendor plan doesn't support more groups/i.test(e.message)) {
                        return {
                          [FORM_ERROR]: maxGroups,
                        };
                      }

                      return {
                        [FORM_ERROR]: couldntSave,
                      };
                    }
                  }}
                />
              </div>
            );
          }}
        </Query>
      )}
    </Mutation>
  );
}
